import {useMutation} from '@apollo/client';
import {
  DeleteCategoryDocument,
  UpdateCategoryDocument,
  useAllCategoryGroupsQuery,
  useSetCategoryGroupMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {
  removeCategoryFromGroups,
  setCategoryGroups,
} from 'src/apolloCache/categoryGroups';
import {removeCategory} from 'src/apolloCache/updateCategories';
import {Button} from 'src/Button';
import {
  validCategoryColorOrNull,
  validCategoryIconOrNull,
} from 'src/categoryAppearance';
import InputGroup from 'src/InputGroup';
import {CategoryAppearanceInput} from 'src/inputs/CategoryAppearanceInput';
import {CategoryGroupSelect} from 'src/inputs/CategoryGroupSelect';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {ApolloCache} from '@apollo/client';
import type {FullCategoryFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type EditCategoryModalUserProps = {
  category: FullCategoryFragment;
  onUpdate?: () => void;
  onDelete?: (category: FullCategoryFragment) => void;
};

export const EditCategoryModal = (
  props: EditCategoryModalUserProps & SystemProps,
) => {
  const {category, onRequestClose} = props;
  const initialIcon = validCategoryIconOrNull(category.icon);
  const initialColor = validCategoryColorOrNull(category.color);
  const [name, setName] = React.useState(category.name);
  const [icon, setIcon] = React.useState<string | null>(initialIcon);
  const [color, setColor] = React.useState<string | null>(initialColor);
  // undefined = untouched (show the stored group); null = "No group"
  const [groupOverride, setGroupOverride] = React.useState<
    string | null | undefined
  >(undefined);
  const [updateCategory, {loading: updateLoading}] = useMutation(
    UpdateCategoryDocument,
  );
  const [deleteCategory, {loading: deleteLoading}] = useMutation(
    DeleteCategoryDocument,
  );
  // groupSaving keeps Save disabled during a group-only save, where
  // updateLoading stays false — otherwise a second submit races two
  // full-array setCategoryGroup writes and the older one can win.
  const [setCategoryGroup, {loading: groupSaving}] =
    useSetCategoryGroupMutation();
  const {data: groupsData} = useAllCategoryGroupsQuery();

  const storedGroupId =
    (groupsData?.allCategoryGroups || []).find((group) =>
      group.categoryIds.includes(category._id),
    )?._id ?? null;
  const groupId = groupOverride === undefined ? storedGroupId : groupOverride;
  const groupChanged =
    groupOverride !== undefined && groupOverride !== storedGroupId;
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Separate from groupChanged so a group-only save doesn't resend the
  // ordinary fields with this form's possibly-stale values.
  const fieldsChanged =
    name !== category.name || icon !== initialIcon || color !== initialColor;
  const hasChanges = fieldsChanged || groupChanged;

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (updateLoading || groupSaving) {
      return;
    }
    setSaveError(null);
    // Sequential on purpose: if these ran concurrently and updateCategory
    // failed (say, a name conflict) while the group change succeeded, the
    // modal would report failure after applying half the save. The group
    // only moves once the field update has committed.
    const save = async () => {
      if (fieldsChanged) {
        await updateCategory({
          variables: {name, id: category._id, icon, color},
        });
      }
      if (groupChanged) {
        await setCategoryGroup({
          variables: {categoryId: category._id, groupId},
          update: (cache, result) => {
            const updated = result.data?.setCategoryGroup;
            if (updated) {
              setCategoryGroups(cache, updated);
            }
          },
        });
      }
    };
    save()
      .then(() => {
        props.onUpdate?.();
        onRequestClose();
      })
      .catch((err: unknown) => {
        // Keep the modal open and say why, instead of an unhandled
        // rejection with the form silently stuck.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        setSaveError((err as Error).message);
      });
  };

  const handleDelete = () => {
    deleteCategory({
      variables: {id: category._id},
      update: (store: ApolloCache<object>) => {
        removeCategory(store, category);
        removeCategoryFromGroups(store, category._id);
      },
    }).then(() => {
      props.onDelete?.(category);
      onRequestClose();
    });
  };

  const footer = (
    <>
      <Button
        variant="destructive"
        onClick={handleDelete}
        disabled={deleteLoading}
      >
        Delete
      </Button>
      <Button
        variant="outline"
        onClick={onRequestClose}
        disabled={updateLoading || groupSaving}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        onClick={handleUpdate}
        disabled={!hasChanges || updateLoading || groupSaving}
      >
        Save
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit category"
      footer={footer}
    >
      <form onSubmit={handleUpdate} className="flex flex-col gap-4">
        {saveError && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {saveError}
          </div>
        )}
        <InputGroup label="Name">
          <TextInput autoFocus value={name} onChange={setName} />
        </InputGroup>

        <CategoryGroupSelect value={groupId} onChange={setGroupOverride} />

        <InputGroup label="Appearance">
          <CategoryAppearanceInput
            name={name}
            icon={icon}
            color={color}
            onIconChange={setIcon}
            onColorChange={setColor}
          />
        </InputGroup>
      </form>
    </Modal>
  );
};
